import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck, ShieldOff, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, errorText, PASSWORD_HINT } from "@/lib/api";
import { TextField } from "@/components/tools/fields";
import { Panel, ErrorText } from "@/components/rc/shared";

const ChangePassword = () => {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const [error, setError] = useState("");
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/rc/password/change", f);
      setF({ current_password: "", new_password: "" });
      setError("");
      toast.success("Password changed. You've been signed out everywhere else.");
    } catch (err) {
      setError(errorText(err));
    }
  };
  return (
    <form onSubmit={submit} noValidate className="max-w-md space-y-4" data-testid="password-change-form">
      <h3 className="font-display text-2xl font-medium">Change password</h3>
      <TextField id="pc-current" label="Current password" type="password" autoComplete="current-password" value={f.current_password} onChange={set("current_password")} testid="password-current" />
      <TextField id="pc-new" label="New password" type="password" autoComplete="new-password" value={f.new_password} onChange={set("new_password")} testid="password-new" />
      <p className="-mt-2 text-sm text-ink-soft">{PASSWORD_HINT}</p>
      <ErrorText error={error} testid="password-change-error" />
      <button type="submit" className="btn btn-brand" data-testid="password-change-submit"><KeyRound size={16} aria-hidden /> Change password</button>
    </form>
  );
};

const Setup = ({ setup, onDone }) => {
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const enable = async (e) => {
    e.preventDefault();
    try {
      await api.post("/rc/mfa/enable", { otp });
      toast.success("Two-step verification is on");
      onDone();
    } catch (err) {
      setError(errorText(err));
    }
  };
  return (
    <form onSubmit={enable} className="grid gap-6 sm:grid-cols-[auto_1fr] sm:items-start" data-testid="mfa-setup-form">
      <div className="w-fit rounded-2xl bg-white p-3"><QRCodeSVG value={setup.uri} size={168} title="Scan with your authenticator app" /></div>
      <div className="space-y-4">
        <p>Scan the code with an authenticator app (for example Microsoft or Google Authenticator), or type this key:</p>
        <p className="break-all rounded-xl bg-paper px-3 py-2 font-mono font-bold" data-testid="mfa-secret">{setup.secret}</p>
        <TextField id="mfa-otp" label="Enter the 6-digit code" inputMode="numeric" autoComplete="one-time-code" value={otp} onChange={setOtp} testid="mfa-otp" />
        <ErrorText error={error} testid="mfa-error" />
        <button type="submit" className="btn btn-brand" data-testid="mfa-enable-submit"><ShieldCheck size={16} aria-hidden /> Turn on</button>
      </div>
    </form>
  );
};

const Disable = ({ onDone }) => {
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const off = async (e) => {
    e.preventDefault();
    try {
      await api.post("/rc/mfa/disable", { otp });
      toast.success("Two-step verification is off");
      onDone();
    } catch (err) {
      setError(errorText(err));
    }
  };
  return (
    <form onSubmit={off} className="max-w-sm space-y-4" data-testid="mfa-disable-form">
      <TextField id="mfa-off-otp" label="Code from your app to turn it off" inputMode="numeric" value={otp} onChange={setOtp} testid="mfa-disable-otp" />
      <ErrorText error={error} testid="mfa-disable-error" />
      <button type="submit" className="btn btn-ghost" data-testid="mfa-disable-submit"><ShieldOff size={16} aria-hidden /> Turn off</button>
    </form>
  );
};

export const Security = ({ enabled, refresh, manage }) => {
  const [setup, setSetup] = useState(null);
  const start = async () => {
    try {
      const { data } = await api.post("/rc/mfa/setup");
      setSetup(data);
    } catch (err) {
      toast.error(errorText(err));
    }
  };
  const done = () => { setSetup(null); refresh(); };
  return (
    <Panel id="ws-security" eyebrow="Signed out after 30 minutes without activity" title="Account security">
      <div className="grid gap-10 lg:grid-cols-2">
        <ChangePassword />
        {manage && (
          <div>
            <h3 className="font-display text-2xl font-medium">Two-step verification</h3>
            <p className="mb-5 mt-2 text-ink-soft" data-testid="mfa-status">
              {enabled ? "On. You'll be asked for a code from your authenticator app each time you sign in." : "Optional now, required in phase 2. Add a code from your phone to your sign-in to protect the workspace."}
            </p>
            {enabled ? <Disable onDone={done} /> : setup ? <Setup setup={setup} onDone={done} /> : (
              <button type="button" onClick={start} className="btn btn-brand" data-testid="mfa-start"><ShieldCheck size={16} aria-hidden /> Set up two-step verification</button>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
};
